import routes from '../../config/routes';

type RoutePermission = {
  code: string;
  name: string;
  module: string;
  description?: string;
};

type RouteLike = {
  path?: string;
  name?: string;
  hideInMenu?: boolean;
  access?: string;
  permission?: RoutePermission | RoutePermission[];
  actions?: RoutePermission | RoutePermission[];
  routes?: RouteLike[];
};

export type PermissionSyncItem = {
  code: string;
  name: string;
  module: string;
  description?: string;
};

export type PermissionMenuNode = {
  key: string;
  title: string;
  permission?: PermissionSyncItem;
  actions: PermissionSyncItem[];
  children: PermissionMenuNode[];
};

export type RouteAccessPermissionMap = Record<string, string[]>;

export const MENU_TITLE_MAP: Record<string, string> = {
  dashboard: '数据看板',
  welcome: '工作台',
  workplace: '工作台',
  comicRankings: '漫画榜单',
  packageMetrics: '按包统计',
  comics: '漫画管理',
  list: '漫画列表',
  recommended: '首页推荐',
  works: '聚合漫画',
  exports: '导出管理',
  displayRules: 'displayRules',
  reportAnalysis: '报表分析',
  retentionAnalysis: '留存分析',
  spiderSync: 'Spider 同步',
  edit: '编辑漫画',
  chapters: '章节管理',
  chapterEdit: '章节编辑',
  tags: '标签管理',
  source: '源站标签',
  master: '主标签',
  translations: '标签翻译',
  logs: 'App 用户',
  blacklist: 'App 用户',
  blackRecords: '黑名单记录',
  comicPushDeliveries: '漫画推送记录',
  versionManagement: '版本管理',
  version: '版本审核',
  ips: 'IP 池管理',
  packages: '包管理',
  growthConfig: '增长配置',
  configManagement: '全局配置',
  featureFlags: 'Feature Flag',
  globalConfig: 'Feature Flag',
  lab: '实验室',
  adConfig: '广告配置',
  adManagement: '广告管理',
  adSlotsConfig: '广告组',
  adGlobalConfigs: '全局广告配置',
  adIds: '广告ID管理',
  adTypes: '广告类型',
  adSlotPositions: '广告位',
  adClientPositions: '客户端位置',
  adSlotPlacementSettings: '广告位全局设置',
  homeGuide: '首页引导位',
  namespaces: '命名空间',
  maintenance: '维护管理',
  readerBugs: 'BUG 管理',
  system: '系统管理',
  appDecor: 'App 装修',
  feedbacks: '用户反馈',
  settings: '配置',
  readerUser: '阅读器反馈',
  summary: '反馈汇总',
  userPolicies: '用户策略',
  whitelist: '白名单',
  users: '后台用户',
  tenants: '租户管理',
  groups: '账号组权限',
  groupPermissions: '客户端权限',
  permissions: '权限目录',
  actionLogs: '操作审计',
  flower: '任务监控',
  cache: '缓存管理',
  encryptedDomains: '混淆域名',
  policyGatewayTestRoutes: '用户转发测试路由',
  notificationBridge: '通知桥接',
  retentionSnapshot: '生成留存快照',
  profiles: '装修方案',
  pools: '漫画池',
  comicLists: '漫单管理',
  profilesNormal: '普通装修',
  profilesChannel: '渠道装修',
  appDecorBase: '基础配置',
  appDecorBanners: 'Banner 管理',
  appDecorRecommended: '首页推荐',
  appDecorCategories: '分类管理',
  appDecorComicLists: '漫单管理',
  appDecorHoneypotPool: '漫画池',
  feedbackSettings: '配置',
  feedbackReader: '阅读器',
  feedbackSummary: '汇总',
  userPoliciesHoneypot: '蜜罐策略',
  userPoliciesRegistration: '注册策略',
  userPoliciesTrusted: '高活跃用户策略',
  whitelistList: '白名单列表',
  whitelistPolicyGateway: '用户转发',
  userPolicyHits: '策略命中记录',
  retentionUser: '用户留存',
  retentionComic: '漫画阅读留存',
  versionVersion: '版本审核',
  versionApps: '应用管理',
  versionIps: 'IP 池管理',
};

const MODULE_SORT_ORDER: Record<string, number> = {
  dashboard: 1,
  content: 2,
  bi: 3,
  tags: 4,
  logs: 5,
  audit_config: 6,
  version_management: 7,
  growth: 8,
  config_management: 9,
  maintenance: 10,
  system: 11,
  users: 12,
  groups: 13,
  permissions: 14,
  audit_logs: 15,
  other: 99,
};

const normalizePermission = (
  item: RoutePermission | undefined,
  route: RouteLike,
): PermissionSyncItem | undefined => {
  const code = item?.code?.trim();
  if (!code) return undefined;
  return {
    code,
    name: item?.name?.trim() || route.name || code,
    module: item?.module?.trim() || route.name || 'other',
    description: item?.description?.trim() || route.path || undefined,
  };
};

const normalizePermissions = (
  items: RoutePermission | RoutePermission[] | undefined,
  route: RouteLike,
): PermissionSyncItem[] => {
  const permissions = Array.isArray(items) ? items : items ? [items] : [];
  return permissions
    .map((item) => normalizePermission(item, route))
    .filter((item): item is PermissionSyncItem => Boolean(item));
};

const collectPermissionsFromNode = (
  node: PermissionMenuNode,
  output: PermissionSyncItem[],
) => {
  if (node.permission) output.push(node.permission);
  node.actions.forEach((item) => {
    output.push(item);
  });
  node.children.forEach((child) => {
    collectPermissionsFromNode(child, output);
  });
};

const buildLegacyPermissionNode = (
  route: RouteLike,
  permissions: PermissionSyncItem[],
) => {
  if (!permissions.length) return { permission: undefined, actions: [] };
  const readLikeIndex = permissions.findIndex((item) =>
    /(:read|\.read)$/.test(item.code),
  );
  const permissionIndex = readLikeIndex >= 0 ? readLikeIndex : 0;
  return {
    permission: permissions[permissionIndex],
    actions: permissions
      .filter((_, index) => index !== permissionIndex)
      .map((item) => {
        const code = item.code?.trim();
        return {
          code,
          name: item.name?.trim() || route.name || code,
          module: item.module?.trim() || route.name || 'other',
          description: item.description?.trim() || route.path || undefined,
        };
      }),
  };
};

const collectPermissionsFromRouteTree = (
  route: RouteLike,
): PermissionSyncItem[] => {
  const ownPermissions = normalizePermissions(route.permission, route);
  const childPermissions = (route.routes || []).flatMap((child) =>
    collectPermissionsFromRouteTree(child),
  );
  return [...ownPermissions, ...childPermissions];
};

const buildMenuTree = (items: RouteLike[]): PermissionMenuNode[] => {
  const nodes: PermissionMenuNode[] = [];
  items.forEach((route) => {
    const declaredPermissions = normalizePermissions(route.permission, route);
    const declaredActions = normalizePermissions(route.actions, route);
    const normalized = declaredActions.length
      ? {
          permission: declaredPermissions[0],
          actions: [...declaredPermissions.slice(1), ...declaredActions],
        }
      : buildLegacyPermissionNode(route, declaredPermissions);
    const children = route.routes?.length ? buildMenuTree(route.routes) : [];
    const hasPermission = Boolean(
      normalized.permission || normalized.actions.length,
    );
    if (route.hideInMenu) {
      if (hasPermission || children.length) {
        const key = route.name || route.path || 'other';
        nodes.push({
          key,
          title: MENU_TITLE_MAP[key] || key,
          permission: normalized.permission,
          actions: normalized.actions,
          children,
        });
      } else {
        nodes.push(...children);
      }
      return;
    }
    if (!hasPermission && !children.length) {
      return;
    }
    const key = route.name || route.path || 'other';
    nodes.push({
      key,
      title: MENU_TITLE_MAP[key] || key,
      permission: normalized.permission,
      actions: normalized.actions,
      children,
    });
  });
  return nodes;
};

const sortMenuTree = (nodes: PermissionMenuNode[]): PermissionMenuNode[] =>
  nodes.map((node) => ({
    ...node,
    children: sortMenuTree(node.children),
  }));

export const buildPermissionMenuTree = () => {
  return sortMenuTree(buildMenuTree(routes as RouteLike[]));
};

export const buildRouteAccessPermissionMap = (): RouteAccessPermissionMap => {
  const map: RouteAccessPermissionMap = {};
  const walk = (items: RouteLike[]) => {
    items.forEach((route) => {
      const permissions = collectPermissionsFromRouteTree(route);
      if (route.access && permissions.length) {
        map[route.access] = Array.from(
          new Set([
            ...(map[route.access] || []),
            ...permissions.map((item) => item.code),
          ]),
        );
      }
      if (route.routes?.length) {
        walk(route.routes);
      }
    });
  };
  walk(routes as RouteLike[]);
  return map;
};

export const collectRoutePermissions = () => {
  const permissions: PermissionSyncItem[] = [];
  buildMenuTree(routes as RouteLike[]).forEach((node) => {
    collectPermissionsFromNode(node, permissions);
  });
  const seen = new Map<string, PermissionSyncItem>();
  permissions.forEach((item) => {
    seen.set(item.code, item);
  });
  return Array.from(seen.values()).sort((left, right) => {
    const leftModule = MODULE_SORT_ORDER[left.module] ?? 999;
    const rightModule = MODULE_SORT_ORDER[right.module] ?? 999;
    if (leftModule !== rightModule) {
      return leftModule - rightModule;
    }
    return left.code.localeCompare(right.code);
  });
};

export const isFrontendMenuPermission = (code: string) => {
  const permissions = collectRoutePermissions();
  return permissions.some((item) => item.code === code);
};
